import * as React from 'react';
import { Helmet } from 'react-helmet';
import { Card, CardBody, Col, Row, Button, Modal, ModalHeader, ModalBody, ModalFooter, Input, Label, FormGroup } from 'reactstrap';
import ThemeContext from '@app/providers/contexts/ThemeContext';
import Page from '@app/components/Page';
import { MdInsertChart, MdAdd, MdDownload, MdCalendarMonth } from 'react-icons/md';
import Loader from '@app/components/Loader';
import './Reporting.css';
import { useHistory } from 'react-router-dom';
import { ReportsService, ReportsResponse, ReportListItem } from '@app/api/reports/reports.service';

// Constants for report types
const REPORT_TYPE = {
  STARLINK_USAGE: 1
};

// Constants for export formats
const EXPORT_FORMAT = {
  CSV: 1
  // Add more formats here in the future
};

interface ThemeContextType {
  theme: string;
  setTheme: (theme: string) => void;
}

const StarlinkUsageReportView: React.FC = () => {
  const { theme } = React.useContext(ThemeContext) as ThemeContextType;
  const history = useHistory();
  const [isLoading, setIsLoading] = React.useState(true);
  const [reports, setReports] = React.useState<ReportListItem[]>([]);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [selectedMonth, setSelectedMonth] = React.useState<string>('');
  const [selectedYear, setSelectedYear] = React.useState<number>(new Date().getFullYear());
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [showDateRange, setShowDateRange] = React.useState(false);
  const [startDate, setStartDate] = React.useState<string>('');
  const [endDate, setEndDate] = React.useState<string>('');
  const [error, setError] = React.useState<string | null>(null);
  
  const toggleModal = () => setIsModalOpen(!isModalOpen);

  // Get list of months for the dropdown
  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  // Get a range of years (current year - 2 to current year)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 3 }, (_, i) => currentYear - i);

  // Load initial reports
  React.useEffect(() => {
    const loadInitialReports = async () => {
      setIsLoading(true);
      try {
        // Fetch reports with appropriate report type
        const response = await ReportsService.getStarlinkUsageReport(
          undefined,
          undefined,
          REPORT_TYPE.STARLINK_USAGE
        );
        
        // Transform all reports into formatted list items
        if (response.reports && response.reports.length > 0) {
          // Sort reports by billing period start date first
          const sortedReports = response.reports.sort((a, b) => {
            return new Date(a.billing_period.start).getTime() - new Date(b.billing_period.start).getTime();
          });
          
          const formattedReports = sortedReports.map((report, index) => {
            //find the original index in the unsorted array for formatReportForList
            const originalIndex = response.reports.findIndex(r => r.id === report.id);
            return ReportsService.formatReportForList(response, originalIndex);
          });
          
          setReports(formattedReports);
        } else {
          setReports([]);
        }
        
        setError(null);
      } catch (err: any) {
        console.error('Failed to load reports:', err);
        // Don't set error state for network errors
        if (!err.message || (err.message && !err.message.includes('Network Error'))) {
          setError(err.message || 'Failed to load reports');
        } else {
          // Just log network errors
          // console.log('Network error occurred, suppressing error banner');
        }
        setReports([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialReports();
  }, []);

  const handleGenerateReport = async () => {
    let reportStartDate: string;
    let reportEndDate: string;

    if (showDateRange) {
      if (!startDate || !endDate) {
        alert('Please select both start and end dates');
        return;
      }
      reportStartDate = startDate;
      reportEndDate = endDate;
    } else {
      if (!selectedMonth || !selectedYear) {
        alert('Please select a month and year');
        return;
      }
      
      // Create date range for the selected month
      const startDateObj = new Date(selectedYear, parseInt(selectedMonth) - 1, 1);
      const endDateObj = new Date(selectedYear, parseInt(selectedMonth), 0);
      
      reportStartDate = startDateObj.toISOString().split('T')[0];
      reportEndDate = endDateObj.toISOString().split('T')[0];
    }

    setIsGenerating(true);
    setError(null);

    try {
      const reportResponse = await ReportsService.getStarlinkUsageReport(
        reportStartDate,
        reportEndDate,
        REPORT_TYPE.STARLINK_USAGE
      );
      
      // Check if the report has sites (data)
      if (reportResponse.reports.length === 0) {
        alert('Usage data is not available for the billing period you selected');
        toggleModal();
        setIsGenerating(false);
        return;
      }
      
      const formattedReport = ReportsService.formatReportForList(reportResponse, 0);
      
      // Close modal
      toggleModal();
      
      // Navigate to the report details page instead of showing the list
      history.push(`/reporting/starlink-usage/report/${formattedReport.id}`);
    } catch (err: any) {
      console.error('Failed to generate report:', err);
      setError(err.message || 'Failed to generate report');
      setIsGenerating(false);
    }
  };

  const handleViewReportDetails = (reportId: string) => {
    history.push(`/reporting/starlink-usage/report/${reportId}`);
  };

  const handleDownloadClick = async (report: ReportListItem, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click from triggering
    
    try {
      // Show loading or disable button (in a real implementation)
      console.log(`Downloading report: ${report.month} ${report.year}`);
      
      // Call the export API to get the download URL with appropriate report type
      const exportResponse = await ReportsService.exportStarlinkUsageReport(
        report.id, 
        EXPORT_FORMAT.CSV, // Use constant instead of hardcoded value
        REPORT_TYPE.STARLINK_USAGE
      );
      
      // Open the download URL in a new tab or trigger download
      if (exportResponse.url) {
        window.open(exportResponse.url, '_blank');
      } else {
        throw new Error('No download URL returned from the server');
      }
    } catch (err: any) {
      console.error('Download failed:', err);
      alert(`Failed to download report: ${err.message || 'Unknown error'}`);
    }
  };

  const formatDate = (dateString: string) => {
    // Handle YYYY-MM-DD format from API
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-').map(Number);

      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
    
    // Handle other date formats with time component
    const fullDateString = dateString.includes('T') ? dateString : `${dateString}T00:00:00Z`;
    const date = new Date(fullDateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const toggleDateRangeMode = () => {
    setShowDateRange(!showDateRange);
  };

  return (
    <>
      <Helmet>
        <title>Starlink Usage Reports</title>
      </Helmet>
      {isLoading ? (
        <Loader />
      ) : (
        <Page
          tag="div"
          className={`cr-page px-3 pt-2 controllers-list ${theme}`}
          title=""
          breadcrumbs={[
            { name: 'Home', active: false, to: '/' },
            { name: 'Dashboard', active: false, to: '/dashboard' },
            { name: 'Reporting', active: false, to: '/reporting' },
            { name: 'Starlink Usage Reports', active: true }
          ]}
          actionButton={
            <Button 
              className="ethica-button-green" 
              onClick={toggleModal}
              style={{ whiteSpace: 'nowrap' }}
            >
              <MdAdd className="me-1" /> Generate New Report
            </Button>
          }
        >
          <Row>
            <Col lg={12} md={12} sm={12} xs={12} className="mb-3">
              <div className="controllers-card-wrapper">
                <Card className="p-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h2 className="mb-0"><strong>Starlink Usage Reports</strong></h2>
                  </div>

                  {error && error !== 'No reports found' && error !== 'Network Error' && error.indexOf('Network Error') === -1 && (
                    <div className="alert alert-danger" role="alert">
                      {error}
                    </div>
                  )}

                  {reports.length === 0 ? (
                    <table className="controllers-list-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th>Report Period</th>
                          <th>Billing Period</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td colSpan={3} className="text-center">No data available</td>
                        </tr>
                      </tbody>
                    </table>
                  ) : (
                    <table className="controllers-list-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                      <thead>
                        <tr>
                          <th style={{ width: '33.33%' }}>
                            <div className="d-flex align-items-center justify-content-start">
                              <strong>Report Period</strong>
                            </div>
                          </th>
                          <th style={{ width: '33.33%' }}>
                            <div className="d-flex align-items-center justify-content-start ms-5">
                              <strong>Billing Period</strong>
                            </div>
                          </th>
                          <th style={{ width: '33.33%' }}>
                            <div className="d-flex align-items-center justify-content-center">
                              <strong>Actions</strong>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {reports.map((report) => (
                          <tr 
                            key={report.id} 
                            className="cursor-pointer"
                            onClick={() => handleViewReportDetails(report.id)}
                          >
                            <td>
                              <div className="d-flex align-items-center justify-content-start">
                                <MdCalendarMonth className="me-2 text-primary" style={{ marginRight: '8px' }} />
                                {report.month} {report.year}
                              </div>
                            </td>
                            <td>
                              <div className="d-flex align-items-center justify-content-start ms-5">
                                {formatDate(report.billingStart)} - {formatDate(report.billingEnd)}
                              </div>
                            </td>
                            <td>
                              <div className="d-flex align-items-center justify-content-center">
                                <Button 
                                  size="sm" 
                                  className="ethica-button-green me-2"
                                  onClick={() => handleViewReportDetails(report.id)}
                                >
                                  View
                                </Button>
                                <Button 
                                  size="sm" 
                                  className="ethica-button-green"
                                  onClick={(e) => handleDownloadClick(report, e)}
                                >
                                  Download
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </Card>
              </div>
            </Col>
          </Row>

          {/* Create Report Modal */}
          <Modal 
            isOpen={isModalOpen} 
            toggle={toggleModal}
            centered
            className="report-modal"
          >
            <ModalHeader toggle={toggleModal}>Generate Starlink Usage Report</ModalHeader>
            <ModalBody>
              <FormGroup className="mb-3">
                <div className="d-flex mb-3">
                  <Label className="me-4">
                    <Input 
                      type="radio" 
                      name="dateMode" 
                      checked={!showDateRange}
                      onChange={() => setShowDateRange(false)}
                    />{' '}
                    Monthly Report
                  </Label>
                  <Label>
                    <Input 
                      type="radio" 
                      name="dateMode" 
                      checked={showDateRange}
                      onChange={() => setShowDateRange(true)}
                    />{' '}
                    Custom Date Range
                  </Label>
                </div>
              </FormGroup>

              {showDateRange ? (
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="startDate">Start Date</Label>
                      <Input 
                        type="date" 
                        id="startDate"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="form-control"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="endDate">End Date</Label>
                      <Input 
                        type="date" 
                        id="endDate"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="form-control"
                      />
                    </FormGroup>
                  </Col>
                </Row>
              ) : (
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="month">Month</Label>
                      <Input 
                        type="select" 
                        id="month"
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        className="form-select"
                      >
                        <option value="">Select Month</option>
                        {months.map((month) => (
                          <option key={month.value} value={month.value}>
                            {month.label}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="year">Year</Label>
                      <Input 
                        type="select" 
                        id="year"
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(Number(e.target.value))}
                        className="form-select"
                      >
                        <option value="">Select Year</option>
                        {years.map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                </Row>
              )}
            </ModalBody>
            <ModalFooter>
              <Button 
                className="ethica-button-black me-2"
                style={{ backgroundColor: 'black', border: 'none' }}
                onClick={toggleModal}
              >
                Cancel
              </Button>
              <Button 
                className="ethica-button-green" 
                onClick={handleGenerateReport}
                disabled={isGenerating}
                style={{ whiteSpace: 'nowrap' }}
              >
                {isGenerating ? 'Generating...' : 'Generate Report'}
              </Button>
            </ModalFooter>
          </Modal>
        </Page>
      )}
    </>
  );
};

export default StarlinkUsageReportView; 